"use client";

/**
 * Header bar for the onboarding flow.
 *
 * Top-aligned bar with brand on the left and controls on the right:
 * language switcher, theme toggle, help (Intercom), and a user menu
 * with Sign Out. Replaces the prior absolute-positioned toolbar.
 */

import { Link } from "react-router-dom";
import { HelpCircle, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DropdownMenu, DropdownMenuItem } from "@speakai/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { useIntercom } from "@/hooks/use-intercom";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { LanguageSelector } from "@/components/layout/language-selector";
import { accountInitials } from "@speakai/common/src/utils/speaker-name";

const INITIALS_FALLBACK = "U";

function UserMenu() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  const displayName = user?.firstName || user?.email || "User";
  const initials = accountInitials([user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email) || INITIALS_FALLBACK;

  return (
    <DropdownMenu
      align="right"
      width="w-56"
      trigger={
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-gradient-from to-gradient-to text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={t("ACTION.OPEN_USER_MENU")}
        >
          {initials}
        </span>
      }
    >
      <div className="px-3 py-2">
        <p className="truncate text-sm font-medium text-foreground">
          {displayName}
        </p>
        {user?.email && user.firstName && (
          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        )}
      </div>
      <div className="my-1 h-px bg-border" />
      <DropdownMenuItem onClick={() => logout()}>
        <LogOut className="mr-2 h-4 w-4" />
        {t("ACTION.SIGN_OUT")}
      </DropdownMenuItem>
    </DropdownMenu>
  );
}

export function OnboardingToolbar() {
  const { t } = useTranslation();
  const { show: showIntercom } = useIntercom();

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background px-4 sm:px-6">
      <Link
        to="/home"
        className="truncate text-lg font-bold tracking-tight text-foreground"
      >
        Speak
      </Link>
      <div className="flex items-center gap-1.5">
        <LanguageSelector />
        <ThemeToggle />
        <button
          type="button"
          onClick={() => showIntercom()}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={t("MENU.HUMAN_SUPPORT")}
        >
          <HelpCircle className="h-4.5 w-4.5" />
        </button>
        <UserMenu />
      </div>
    </header>
  );
}
